import { Link } from "react-router"
import { ArrowUpRight, AlertTriangle, CheckCircle, FileCheck02 } from "@untitledui/icons"

import { PaneSkeleton } from "./skeletons"
import type { ApiEstimate } from "@/lib/api/types"
import type { TaskRow } from "@/lib/api/tasks"
import { estimateVerdict } from "@/lib/estimates/estimate-verdict"
import { cx } from "@/lib/utils/cx"

// The estimate, two ways. `full` is the REVIEW: what she needs to say "good
// to go" without opening another page — the damage photos big, every line
// with its parts and labour, the range at the bottom, the flag at the top.
// Compact is the rail's quoting card beside a call: the range, the flag,
// the lines, so she can answer "how much?" while they're on the phone.

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`
}

function pretty(s: string): string {
  return s.replace(/_/g, " ").toLowerCase()
}

function hours(n: number): string {
  return `${Number.isInteger(n) ? n : n.toFixed(1)} h`
}

const LINK =
  "inline-flex items-center gap-0.5 h-9 px-2 rounded-md text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors duration-150 ease-out-quart motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20"

function Verdict({ estimate, className }: { estimate: ApiEstimate; className?: string }) {
  const verdict = estimateVerdict(estimate)
  return (
    <p
      className={cx(
        "inline-flex items-center gap-1.5 text-[13px] font-medium",
        verdict.level === "clean" ? "text-emerald-700" : "text-amber-700",
        className,
      )}
    >
      {verdict.level === "clean" ? (
        <>
          <CheckCircle className="size-3.5" aria-hidden /> Looks clean
        </>
      ) : (
        <>
          <AlertTriangle className="size-3.5" aria-hidden /> Take a look: {verdict.reasons.join(" · ")}
        </>
      )}
    </p>
  )
}

export function EstimatePane({ estimate, row, full = false }: { estimate: ApiEstimate | undefined; row: TaskRow; full?: boolean }) {
  if (!estimate) return <PaneSkeleton />
  const items = estimate.lineItems ?? []
  const images = (estimate.images ?? []).filter((i) => i.isActive !== false)
  const links = (
    <div className="flex items-center -mr-2">
      <Link to={`/estimates/${row.estimateId}`} className={LINK}>
        Open <ArrowUpRight className="size-3" aria-hidden />
      </Link>
      <Link to={`/estimates/${row.estimateId}/edit`} className={LINK}>
        Edit <ArrowUpRight className="size-3" aria-hidden />
      </Link>
    </div>
  )

  if (full) {
    const totalHours = items.reduce((sum, li) => sum + (li.manHours ?? 0), 0)
    const [hero, ...rest] = images
    return (
      <div>
        <div className="flex items-center justify-between gap-3">
          <Verdict estimate={estimate} />
          {links}
        </div>

        {/* The photos — the first one big, because that is what "look at it" means. */}
        {hero ? (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-1.5">
            <Link to={`/estimates/${row.estimateId}`} className="block aspect-[4/3] rounded-lg overflow-hidden bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20">
              <img src={hero.src} alt={hero.description ?? `${hero.side} view`} className="size-full object-cover" />
            </Link>
            {rest.length > 0 && (
              <ul className="grid grid-cols-3 sm:grid-cols-2 gap-1.5 content-start">
                {rest.slice(0, 6).map((img) => (
                  <li key={img.id} className="aspect-[4/3] rounded-md overflow-hidden bg-zinc-100">
                    <img src={img.src} alt={img.description ?? `${img.side} view`} className="size-full object-cover" loading="lazy" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="mt-3 text-[13px] text-zinc-500">No photos on this estimate.</p>
        )}

        {/* Every line, with what's under it. */}
        <table className="mt-4 w-full text-[14px]">
          <thead>
            <tr className="text-[12.5px] text-zinc-500 border-b border-zinc-200/80">
              <th className="text-left font-medium pb-2">Work</th>
              <th className="text-right font-medium pb-2 w-16">Hours</th>
              <th className="text-right font-medium pb-2 w-36">Range</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200/80">
            {items.map((li) => {
              const ops = li.operations ?? []
              return (
                <tr key={li.id} className="align-top">
                  <td className="py-2.5 pr-3">
                    <p className="text-zinc-900 capitalize">
                      {pretty(li.component)}
                      {li.action && <span className="text-zinc-500 normal-case"> · {li.action.toLowerCase()}</span>}
                      {li.structuralInvolvement && <span className="ml-1.5 text-[12px] font-semibold text-amber-700 normal-case">structural</span>}
                    </p>
                    {ops.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[13px] text-zinc-600">
                        {ops.map((op, i) => (
                          <li key={op.id ?? i} className="flex items-baseline justify-between gap-3">
                            <span className="truncate">{op.description}</span>
                            <span className="tabular-nums shrink-0 text-zinc-500">
                              {op.laborHours ? hours(op.laborHours) : ""}
                              {op.laborHours && op.partPrice ? " · " : ""}
                              {op.partPrice ? money(op.partPrice) : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-2.5 text-right tabular-nums text-zinc-600">{li.manHours ? hours(li.manHours) : "—"}</td>
                  <td className="py-2.5 text-right tabular-nums text-zinc-900">
                    {li.min != null && li.max != null ? `${money(li.min)} – ${money(li.max)}` : li.avg != null ? money(li.avg) : "—"}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-zinc-900">
              <td className="pt-2.5 text-[13px] text-zinc-500">
                {items.length} {items.length === 1 ? "line" : "lines"}
              </td>
              <td className="pt-2.5 text-right tabular-nums text-zinc-600">{totalHours ? hours(totalHours) : "—"}</td>
              <td className="pt-2.5 text-right tabular-nums text-[16px] font-semibold text-zinc-900">
                {money(estimate.min ?? 0)} – {money(estimate.max ?? 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="inline-flex items-center gap-2">
          <FileCheck02 className="size-4 text-zinc-500" aria-hidden />
          <h3 className="text-[15px] font-semibold text-zinc-900">The estimate</h3>
        </div>
        {links}
      </div>
      <p className="text-[18px] font-semibold text-zinc-900 tabular-nums tracking-tight">
        {money(estimate.min ?? 0)} – {money(estimate.max ?? 0)}
      </p>
      <Verdict estimate={estimate} className="mt-1" />
      {images.length > 0 && (
        <ul className="mt-3.5 grid grid-cols-3 gap-1.5">
          {images.slice(0, 6).map((img) => (
            <li key={img.id} className="aspect-[4/3] rounded-md overflow-hidden bg-zinc-100">
              <img src={img.src} alt={img.description ?? `${img.side} view`} className="size-full object-cover" loading="lazy" />
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && (
        <ul className="mt-3.5 text-[14px] text-zinc-700 divide-y divide-zinc-200/80 border-t border-zinc-200/80">
          {items.slice(0, 6).map((li) => (
            <li key={li.id} className="py-2 flex items-baseline justify-between gap-3">
              <span className="capitalize truncate">
                {pretty(li.component)}
                {li.action && <span className="text-zinc-500 normal-case"> · {li.action.toLowerCase()}</span>}
              </span>
              {li.avg != null && <span className="text-zinc-500 tabular-nums shrink-0">{money(li.avg)}</span>}
            </li>
          ))}
          {items.length > 6 && <li className="py-2 text-zinc-500">+{items.length - 6} more</li>}
        </ul>
      )}
    </div>
  )
}
